import { CreateOrganizationProductRequest, GetPublicOrganizationProductResponse, OrganizationProductStatus, PaymentTargetType, PurchaseItemRequest } from '../types';
import { LitElement, PropertyValues, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { formatPrice, getCurrencySymbol, getPriceCentsFromProduct, getPriceStringFromProduct, getProductById } from '../helpers/api/products-api';
import { getSubscriptionById, get_purchases_for_entity } from '../helpers/api/purchases-api';

import challengeData from '../helpers/state/challenge-data';
import { getQueryStringParameters } from '../helpers/urls';
import { isDefined } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';

@customElement('pre-checkout-component')
export class PreCheckoutComponent extends LitElement {

    @property({ type: Array })
    product_ids: string[] = [];

    @property({ type: String })
    organization_id: string = '';

    @property({ type: String })
    payment_target_type: PaymentTargetType = PaymentTargetType.Form;

    // For Edit View to load purchased products
    @property({ type: Boolean })
    edit_view: boolean = false;

    @property({ type: Boolean })
    with_button: boolean = false;

    @property({ type: Boolean })
    minified: boolean = false;

    @state()
    created_item_id?: string;

    @state()
    created_item_key?: string;

    @state()
    isPreview: boolean = false;

    @state()
    products: GetPublicOrganizationProductResponse[] = [];

    @state()
    purchasedProductIds: Set<string> = new Set<string>();

    @state()
    cancelledProductIds: Set<string> = new Set<string>();

    @state()
    pendingProduct: CreateOrganizationProductRequest | undefined;

    @state()
    productsLoaded: boolean = false;

    @state()
    private lastLoadedProductIds: string = '';

    @state()
    private lastLoadedOrgId: string = '';

    @state()
    remainingLineItems: PurchaseItemRequest[] = [];

    @state()
    donationAmountCents: Map<string, number> = new Map();

    @state()
    isCustomDonationAmount: Map<string, boolean> = new Map();

    protected async updated(_changedProperties: PropertyValues): Promise<void> {
        if (_changedProperties.has('product_ids') || _changedProperties.has('organization_id')) {
            const currentProductIds = this.product_ids.join(',');
            const currentOrgId = this.organization_id;

            // Only load if the actual values have changed
            if (currentProductIds !== this.lastLoadedProductIds || currentOrgId !== this.lastLoadedOrgId) {
                this.lastLoadedProductIds = currentProductIds;
                this.lastLoadedOrgId = currentOrgId;
                await this.loadProducts();
                if (this.edit_view && isDefined(this.created_item_id)) {
                    await this.getCompletedPurchases();
                    this.requestUpdate();
                }
            }
        }

        if (_changedProperties.has('edit_view') || _changedProperties.has('created_item_id')) {
            if (this.edit_view && isDefined(this.created_item_id)) {
                await this.getCompletedPurchases();
                this.requestUpdate();
            }
        }
    }

    async loadProducts() {
        if (!this.organization_id || this.product_ids.length === 0) {
            if (this.pendingProduct) {
                this.products = [ {
                    _id: { $oid: 'pending' },
                    name: this.pendingProduct.name,
                    description: this.pendingProduct.description,
                    pricing_structure: this.pendingProduct.pricing_structure,
                    organization_id: { $oid: this.organization_id },
                    statement_descriptor: this.pendingProduct.statement_descriptor,
                    item_details: this.pendingProduct.item_details,
                    support_email: this.pendingProduct.support_email,
                    currency_code: this.pendingProduct.currency_code.toUpperCase(),
                    proxi_fee_structure: 'none',
                    tax_structure: 'none',
                    status: OrganizationProductStatus.Active,
                } ];
                this.productsLoaded = true;
            }
            this.products = [];
            this.productsLoaded = true;
            return;
        }

        try {
            const newProducts: GetPublicOrganizationProductResponse[] = [];
            for (const productId of this.product_ids) {
                const response = await getProductById(productId);
                if (isDefined(response) && response.status === 'active' && !newProducts.find(p => p._id.$oid === response._id.$oid)) {
                    newProducts.push(response);
                }
            }
            this.products = newProducts;
            if (this.edit_view && isDefined(this.created_item_id)) {
                const queryString = getQueryStringParameters();
                this.created_item_key = this.payment_target_type === PaymentTargetType.Form ? queryString.get('point_key') ?? topicData.topicKey :
                    this.payment_target_type === PaymentTargetType.ActivatedChallenge ? challengeData.activatedChallengeKey ?? queryString.get('activated_challenge_key') ?? undefined : undefined;
                await this.getCompletedPurchases();
            }
            this.updateRemainingLineItems();
            this.productsLoaded = true;
        } catch (error) {
            console.error('Error loading products:', error);
            this.productsLoaded = false;
        }
    }

    async getCompletedPurchases() {
        if (this.edit_view && isDefined(this.created_item_id)) {
            try {
                const completed_purchases = await get_purchases_for_entity(
                    this.created_item_id,
                    this.payment_target_type,
                    this.created_item_key
                );
                const purchasedProductIds = new Set<string>();
                const cancelledProductIds = new Set<string>();

                for (const purchase of completed_purchases) {
                    if (purchase.status === 'completed') {
                        purchase.purchase_items.forEach(item => {
                            // Check if this purchase has a subscription
                            if (purchase.subscription_id) {
                                // For subscriptions, we need to check subscription status
                                // For now, mark as purchased - will be refined when we get subscription details
                                purchasedProductIds.add(item.product_id.$oid);
                            } else {
                                // One-time purchase
                                purchasedProductIds.add(item.product_id.$oid);
                            }
                        });
                    }
                }

                // Now check for cancelled subscriptions
                const subscriptionIds = new Set(completed_purchases
                    .filter(p => p.subscription_id)
                    .map(p => p.subscription_id));

                if (subscriptionIds.size > 0) {
                    for (const subscriptionId of subscriptionIds) {
                        try {
                            const subscription = await getSubscriptionById(subscriptionId ?? '', this.organization_id);
                            if (subscription.organization_subscription.status === 'cancelled') {
                                // Find products for this subscription and move to cancelled
                                const subscriptionPurchases = completed_purchases.filter(p => p.subscription_id === subscriptionId);
                                for (const purchase of subscriptionPurchases) {
                                    purchase.purchase_items.forEach(item => {
                                        purchasedProductIds.delete(item.product_id.$oid);
                                        cancelledProductIds.add(item.product_id.$oid);
                                    });
                                }
                            }
                        } catch (error) {
                            console.error(`Failed to fetch subscription ${subscriptionId ?? ''}:`, error);
                        }
                    }
                } else {
                    // No subscriptions to check
                }

                this.purchasedProductIds = purchasedProductIds;
                this.cancelledProductIds = cancelledProductIds;
                this.updateRemainingLineItems(); // Update computed property
                this.dispatchEvent(new CustomEvent('purchased-products-loaded'));
            } catch (error) {
                console.error('Failed to fetch completed purchases:', error);
            }
        } else {
            this.purchasedProductIds = new Set<string>();
            this.cancelledProductIds = new Set<string>();
            this.updateRemainingLineItems(); // Update computed property
        }
    }

    private updateRemainingLineItems() {
        const lineItems: PurchaseItemRequest[] = [];
        for (const product of this.products) {
            if (this.purchasedProductIds.has(product._id.$oid)) {
                continue; // Skip already purchased products
            }
            // if it is a donation product and there is no donation amount set, skip adding it to line items
            if ('Donation' in product.pricing_structure && !this.donationAmountCents.has(product._id.$oid)) {
                // if donation is required, set to first suggested amount
                if (product.pricing_structure.Donation.required) {
                    const suggestedAmounts = product.pricing_structure.Donation.suggested_amounts_cents;
                    if (suggestedAmounts.length > 0) {
                        this.donationAmountCents.set(product._id.$oid, suggestedAmounts[0]);
                    } else {
                        this.donationAmountCents.set(product._id.$oid, product.pricing_structure.Donation.min_amount_cents);
                    }
                } else {
                    continue;
                }
            }
            lineItems.push({
                product_id: product._id.$oid,
                quantity: 1,
                custom_amount_cents: ('Donation' in product.pricing_structure) ? this.donationAmountCents.get(product._id.$oid) : undefined,
            });
        }
        this.remainingLineItems = lineItems;
    }

    get totalCostString(): string {
        let total = 0;
        let currencyCode = 'USD'; // Default

        const currencies = new Set<string>();
        for (const product of this.products) {
            if (this.purchasedProductIds.has(product._id.$oid)) {
                continue; // Skip already purchased products
            }

            // Use the first product's currency code as the display currency
            if (currencyCode === 'USD' && product.currency_code) {
                currencyCode = product.currency_code;
            }

            if ('Fixed' in product.pricing_structure) {
                total += getPriceCentsFromProduct(product);
            }
            if ('Donation' in product.pricing_structure && isDefined(this.donationAmountCents)) {
                total += this.donationAmountCents.get(product._id.$oid) ?? 0;
            }
            if ('Recurring' in product.pricing_structure) {
                total += getPriceCentsFromProduct(product);
            }
            currencies.add(product.currency_code.trim().toLowerCase());
        }

        if (currencies.size > 1) {
            return 'Error: Multiple Currencies';
        }

        return formatPrice(total, currencyCode);
    }

    get totalCostCents(): number {
        let total = 0;

        const currencies = new Set<string>();
        for (const product of this.products) {
            if (this.purchasedProductIds.has(product._id.$oid)) {
                continue; // Skip already purchased products
            }

            if ('Fixed' in product.pricing_structure) {
                total += getPriceCentsFromProduct(product);
            }
            if ('Donation' in product.pricing_structure && isDefined(this.donationAmountCents)) {
                total += this.donationAmountCents.get(product._id.$oid) ?? 0;
            }
            if ('Recurring' in product.pricing_structure) {
                total += getPriceCentsFromProduct(product);
            }
            currencies.add(product.currency_code.trim().toLowerCase());
        }
        return total;
    }

    get checkoutButtonText(): string {
        // Determine button text based on pricing structure
        const hasDonation = this.products.some(p => 'Donation' in p.pricing_structure && !this.purchasedProductIds.has(p._id.$oid));
        const hasRecurring = this.products.some(p => 'Recurring' in p.pricing_structure && !this.purchasedProductIds.has(p._id.$oid));
        const hasFixed = this.products.some(p => 'Fixed' in p.pricing_structure && !this.purchasedProductIds.has(p._id.$oid));

        if (hasRecurring) {
            return `Subscribe - ${this.totalCostString}`;
        } else if (hasDonation && !hasFixed) {
            return `Donate - ${this.totalCostString}`;
        } else {
            return `Pay - ${this.totalCostString}`;
        }
    }

    get minifiedButtonText(): string {
        // Determine button text for minified mode
        const hasDonation = this.products.some(p => 'Donation' in p.pricing_structure && !this.purchasedProductIds.has(p._id.$oid));
        const hasRecurring = this.products.some(p => 'Recurring' in p.pricing_structure && !this.purchasedProductIds.has(p._id.$oid));
        const hasFixed = this.products.some(p => 'Fixed' in p.pricing_structure && !this.purchasedProductIds.has(p._id.$oid));

        if (hasRecurring) {
            return 'Register Now';
        } else if (hasDonation && !hasFixed) {
            return 'Donate Now';
        } else {
            return 'Pay Now';
        }
    }

    private handleCheckoutClick() {
        this.dispatchEvent(new CustomEvent('checkout-clicked'));
    }

    private handleExpandClick() {
        this.minified = false;
    }

    render() {
        if (!this.productsLoaded) {
            return html`<p>Loading...</p>`;
        }

        // Minified mode - show just expand button
        if (this.minified) {
            return html`
                <div class="pre-checkout-container minified">
                    <form-button
                        @click=${this.handleExpandClick}
                        size="lg"
                        variant="outline"
                    >
                        ${this.minifiedButtonText}
                    </form-button>
                </div>
            `;
        }

        // Full mode - show all products and details
        return html`
            <div class="pre-checkout-container">
                ${this.products.length > 0 ?
                    html`${this.products.map(product => html`
                    <div class="product-card">
                        <h3>${product.name}</h3>
                        <p>${product.description}</p>
                        ${this.renderPriceTypeInfo(product)}
                        ${topicData.topic?.is_writer === true && !this.isPreview ? html`<span style="color: red;">Overriden (do not need to pay) because you are an admin</span>` : html``}
                        ${this.with_button && this.remainingLineItems.length > 0 ? html`
                        <div class="checkout-button-container">
                            <form-button
                                @click=${this.handleCheckoutClick}
                                size="lg"
                            >
                                ${this.checkoutButtonText}
                            </form-button>
                        </div>
                    ` : ''}
                    </div>
                `)}
                ` : ''}
            </div>
        `;
    }

    renderPriceTypeInfo(product: GetPublicOrganizationProductResponse) {
        if (this.products.length === 0) {
            return html``;
        }
        if ('Fixed' in product.pricing_structure) {
            return html`
                ${this.purchasedProductIds.has(product._id.$oid) ? html`<strong style="color: green;">Already Purchased</strong>` : ''}
                ${this.cancelledProductIds.has(product._id.$oid) ? html`<strong style="color: orange;">Purchased - Subscription Cancelled</strong>` : ''}
                <p>Price: ${getPriceStringFromProduct(product)}</p>
            `;
        } else if ('Donation' in product.pricing_structure) {
            const donationPricing = product.pricing_structure.Donation;
            return html`
                <div class="donation-amounts">
                    <span>Suggested Amounts:</span>
                    <div class="donation-buttons" style="display: flex;gap: 10px;align-items: center;justify-content: center;padding: 5px;flex-wrap: wrap;">
                        ${this.donationAmountCents.get(product._id.$oid) !== undefined && !donationPricing.required ? html`
                            <form-button
                                size="sm"
                                class="donation-button"
                                variant="outline"
                                @click=${() => {
                                    this.donationAmountCents.delete(product._id.$oid);
                                    this.requestUpdate();
                                    this.updateRemainingLineItems();
                                }}
                            >
                                No Donation
                            </form-button>
                        ` : ''}
                        ${donationPricing.suggested_amounts_cents.map(amount =>
                            this.donationAmountCents.get(product._id.$oid) === amount
                                ? html`
                                    <form-button
                                        size="sm"
                                        class="donation-button"
                                        @click=${() => {
                                            this.donationAmountCents.set(product._id.$oid, amount);
                                            this.isCustomDonationAmount.set(product._id.$oid, false);
                                            this.requestUpdate();
                                            this.updateRemainingLineItems();
                                        }}
                                    >
                                        ${formatPrice(amount, product.currency_code)}
                                    </form-button>`
                                : html`
                                    <form-button
                                        size="sm"
                                        variant="outline"
                                        class="donation-button"
                                        @click=${() => {
                                            this.donationAmountCents.set(product._id.$oid, amount);
                                            this.isCustomDonationAmount.set(product._id.$oid, false);
                                            this.requestUpdate();
                                            this.updateRemainingLineItems();
                                        }}
                                    >
                                        ${formatPrice(amount, product.currency_code)}
                                    </form-button>`
                        )}
                        ${donationPricing.allow_custom_amount ? html`
                            ${this.isCustomDonationAmount.get(product._id.$oid) === true ? html`
                                <form-button
                                    size="sm"
                                    class="donation-button"
                                    @click=${() => {
                                        this.isCustomDonationAmount.set(product._id.$oid, false);
                                        this.donationAmountCents.set(product._id.$oid, donationPricing.suggested_amounts_cents[0]);
                                        this.requestUpdate();
                                        this.updateRemainingLineItems();
                                    }}
                                >
                                    Remove Custom Amount
                                </form-button>
                            ` : html`
                                <form-button
                                    size="sm"
                                    variant="outline"
                                    class="donation-button"
                                    @click=${() => {
                                        this.isCustomDonationAmount.set(product._id.$oid, true);
                                        this.donationAmountCents.delete(product._id.$oid);
                                        this.requestUpdate();
                                        this.updateRemainingLineItems();
                                    }}
                                >
                                    Custom Amount
                                </form-button>
                            `}
                        ` : ''}
                    </div>
                    ${this.isCustomDonationAmount.get(product._id.$oid) === true ? html`
                        <proxi-textfield
                            type="number"
                            outlined
                            min="${donationPricing.min_amount_cents / 100}"
                            max="${donationPricing.max_amount_cents / 100}"
                            placeholder="Custom Amount"
                            prefix="${getCurrencySymbol(product.currency_code)}"
                            @input=${(e: Event) => {
                                const input = e.target as HTMLInputElement;
                                const customAmount = parseFloat(input.value);
                                if (!isNaN(customAmount) && customAmount > 0) {
                                    this.donationAmountCents.set(product._id.$oid, Math.round(customAmount * 100));
                                    this.requestUpdate();
                                    this.updateRemainingLineItems();
                                }
                            }}
                        ></proxi-textfield>
                    ` : ''}
                    ${donationPricing.required ? html`<p style="color: red;">Minimum Donation of ${formatPrice(donationPricing.min_amount_cents, product.currency_code)} required to proceed.</p>` : ''}
                </div>
            `;
        } else if ('Recurring' in product.pricing_structure) {
            return html`
            ${this.purchasedProductIds.has(product._id.$oid) ? html`<strong style="color: green;">Already Purchased</strong>` : ''}
            ${this.cancelledProductIds.has(product._id.$oid) ? html`<strong style="color: orange;">Purchased - Subscription Cancelled</strong>` : ''}
            <p>Subscription Price: ${getPriceStringFromProduct(product)}</p>
            <p>After checkout you will receive a link to a page with your receipt, which is where you can manage or cancel your subscription at any time.</p>`;
        } else {
            return html`<p>Unknown Pricing</p>`;
        }
    }

    static get styles() {
        return css`
            .pre-checkout-container {
                display: flex;
                flex-direction: column;
                gap: 16px;

            }
            .product-card {
                border: 1px solid #ccc;
                padding: 12px;
                border-radius: 8px;
            }
            .product-card h3 {
                margin: 0 0 4px 0;
            }

            p {
                margin: 4px 0;
            }

            .donation-amounts {
                margin-top: 8px;
            }

            .checkout-button-container {
                display: flex;
                justify-content: center;
                margin-top: 8px;
            }

            .donation-button {
                cursor: pointer;
            }

            .donation-button.selected {
                background-color: var(--main-secondary-color);
                color: white;
            }
        `;
    }
}